<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header titleIcon="notes"
    title="Agent Prompts"
    subtitle="How the agent reads documents and judges answers, per country. A country with no prompt of its own uses the built-in one."
  />

  @if (error) {
  <div
    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200"
    role="alert"
  >
    {{ error }}
  </div>
  }

  @if (notice) {
  <output
    class="block rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300"
  >
    {{ notice }}
  </output>
  }

  @if (showEditor) {
  <div
    class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-[#191e3a] dark:bg-[#0e1726]"
  >
    <div class="grid gap-4 md:grid-cols-3">
      <label class="block">
        <span class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Prompt</span>
        <select
          class="form-select w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
          [(ngModel)]="draft.code"
          name="code"
          [disabled]="editing !== null"
        >
          @for (c of catalog; track c.code) {
          <option [value]="c.code">{{ c.code }}</option>
          }
        </select>
        <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
          {{ specFor(draft.code)?.description }}
        </span>
      </label>

      <label class="block">
        <span class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >Country pack</span
        >
        <select
          class="form-select w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
          [(ngModel)]="draft.country_pack"
          name="country_pack"
          [disabled]="editing !== null"
        >
          <option value="">— Select —</option>
          @for (o of countryPackSelectOptions; track o.value) {
          <option [value]="o.value">{{ o.label }}</option>
          }
        </select>
        <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400"
          >Blank = the fallback used by every country.</span
        >
      </label>

      <label class="block">
        <span class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Locale</span>
        <input
          class="form-input w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
          [(ngModel)]="draft.locale"
          name="locale"
          [disabled]="editing !== null"
        />
      </label>
    </div>

    <div class="mt-4">
      <span class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Body</span>
      <textarea
        class="form-textarea w-full rounded-xl border-gray-200 bg-white font-mono text-xs dark:border-[#191e3a] dark:bg-[#0e1726]"
        rows="14"
        [(ngModel)]="draft.body"
        name="body"
      ></textarea>
    </div>

    <div class="mt-3 rounded-xl bg-gray-50 p-3 text-xs dark:bg-white/5">
      <p class="font-medium text-gray-700 dark:text-gray-300">
        Placeholders you may use ({{ allowedVariables.length }})
      </p>
      @if (!allowedVariables.length) {
      <p class="mt-1 text-gray-500 dark:text-gray-400">
        None — this prompt takes no variables.
      </p>
      } @else {
      <ul class="mt-1 space-y-1">
        @for (v of allowedVariables; track v.name) {
        <li class="text-gray-600 dark:text-gray-400">
          <code class="text-[#303030] dark:text-gray-200">{{ placeholderFor(v.name) }}</code>
          @if (!v.required) {
          <span> (optional)</span>
          }
          — {{ v.description }}
        </li>
        }
      </ul>
      }
    </div>

    <div class="mt-4 flex gap-2">
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 disabled:opacity-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        [disabled]="saving"
        (click)="save()"
      >
        {{ saving ? 'Saving…' : 'Save draft' }}
      </button>
      @if (editing && isDraft(editing)) {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full bg-[#303030] px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-50 dark:bg-gray-900 dark:hover:bg-black"
        [disabled]="saving"
        (click)="publish(editing)"
      >
        Publish
      </button>
      }
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="cancel()"
      >
        Close
      </button>
    </div>
  </div>
  }

  <app-list-module-surface
    [config]="listSurfaceConfigView"
    [filters]="listFilters"
    [loading]="loading"
    [meta]="paginationMeta"
    [rowCount]="pagedRecords.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    <ng-container toolbarActions>
      @if (view === 'active') {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="newPrompt()"
      >
        <icon-plus-circle class="size-5" />
        <span>New prompt</span>
      </button>
      }
      @for (tab of toolbarTabs; track tab.key) {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium shadow-none"
        [class.border-gray-300]="activeToolbarTab !== tab.key"
        [class.bg-white]="activeToolbarTab !== tab.key"
        [class.text-black]="activeToolbarTab !== tab.key"
        [class.hover:bg-gray-50]="activeToolbarTab !== tab.key"
        [class.dark:border-[#191e3a]]="activeToolbarTab !== tab.key"
        [class.dark:bg-[#0e1726]]="activeToolbarTab !== tab.key"
        [class.dark:text-white]="activeToolbarTab !== tab.key"
        [class.dark:hover:bg-white/5]="activeToolbarTab !== tab.key"
        [class.border-[#303030]]="activeToolbarTab === tab.key"
        [class.bg-[#303030]]="activeToolbarTab === tab.key"
        [class.text-white]="activeToolbarTab === tab.key"
        [class.dark:bg-gray-900]="activeToolbarTab === tab.key"
        (click)="onToolbarTabChange(tab.key)"
      >
        {{ tab.label }}
      </button>
      }
    </ng-container>

    <app-list-module-table
      listTable
      variant="surface"
      [columns]="columns"
      [rows]="pagedRecords"
      [loading]="loading"
      [emptyMessage]="emptyMessage"
      rowTrackKey="id"
    >
      <ng-template listCellDef="code" let-row="row">
        <span class="font-medium text-gray-900 dark:text-white">{{ row['code'] }}</span>
      </ng-template>

      <ng-template listCellDef="country_pack" let-row="row">
        {{ row['country_pack'] || 'All countries' }}
      </ng-template>

      <ng-template listCellDef="locale" let-row="row">
        {{ row['locale'] }}
      </ng-template>

      <ng-template listCellDef="version" let-row="row">
        v{{ row['version'] }}
      </ng-template>

      <ng-template listCellDef="deleted_at" let-row="row">
        {{ row['deleted_at'] }}
      </ng-template>

      <ng-template listCellDef="status" let-row="row">
        <div
          class="inline-flex items-center rounded-full py-1 px-3"
          [ngClass]="statusBadgeClass(row['status'])"
        >
          {{ statusLabel(row['status']) }}
        </div>
      </ng-template>

      <ng-template listCellDef="actions" let-row="row">
        <div class="inline-flex items-center justify-end gap-1">
          @if (view === 'active') {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
            [title]="isDraft(row) ? 'Edit' : 'View'"
            [attr.aria-label]="isDraft(row) ? 'Edit' : 'View'"
            (click)="edit(row)"
          >
            @if (isDraft(row)) {
            <icon-edit class="h-4 w-4" />
            } @else {
            <icon-eye class="h-4 w-4" />
            }
          </button>
          @if (isDraft(row)) {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-blue-500 hover:bg-blue-500/10"
            title="Publish"
            aria-label="Publish"
            (click)="publish(row)"
          >
            <icon-send class="h-4 w-4" />
          </button>
          }
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-danger hover:bg-danger/10"
            title="Delete"
            aria-label="Delete"
            (click)="onDelete(row)"
          >
            <icon-trash-lines class="h-4 w-4" />
          </button>
          } @else {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#b8860b] hover:bg-[#FFD85F]/20"
            title="Restore"
            aria-label="Restore"
            (click)="onRestore(row)"
          >
            <icon-restore class="h-4 w-4" />
          </button>
          }
        </div>
      </ng-template>
    </app-list-module-table>
  </app-list-module-surface>
</div>

<app-delete-confirm-modal
  [open]="showDeleteModal"
  heading="Delete prompt?"
  [entityName]="deleteEntityName"
  entityNoun="prompt"
  message="This will remove the prompt version from the list. Published prompts will no longer be served to the AI service."
  confirmLabel="Delete"
  loadingLabel="Deleting…"
  [loading]="deleteLoading()"
  [error]="deleteError"
  variant="permanent"
  (cancelled)="closeDeleteModal()"
  (confirmed)="submitDelete()"
/>

<app-restore-confirm-modal
  [open]="showRestoreModal"
  heading="Restore prompt?"
  [entityName]="restoreEntityName"
  activeListLabel="agent prompts list"
  [loading]="restoreLoading()"
  (cancelled)="closeRestoreModal()"
  (confirmed)="submitRestore()"
/>
